import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Radio, SatelliteDish } from "lucide-react";
import Seo from "../components/Seo";
import { Pill, SectionHead, PrimaryLink, GhostLink, CTABand, NetworkNote } from "../components/bits";
import DecisionSelector from "../components/DecisionSelector";
import { DecisionGraphic } from "../components/diagrams";
import { PRODUCTS, productList } from "../content/products";
import { USE_CASES, ARTICLES } from "../content/pages";

export default function Home() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "GPS Smart Labels",
    url: "https://gpssmartlabels.com",
    publisher: { "@type": "Organization", name: "GoAndTrack" },
  };

  return (
    <>
      <Seo path="/" title="Bluetooth & 4G GPS Smart Labels" jsonLd={jsonLd} />
      {/* Hero */}
      <section className="border-b border-border" data-testid="home-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 sm:px-8 sm:py-28 lg:grid-cols-[1.15fr_1fr]">
          <div>
          <p className="font-mono text-xs font-medium uppercase tracking-widest text-primary">GoAndTrack · Smart Labels</p>
          <h1 className="mt-4 max-w-4xl text-4xl font-semibold tracking-tight text-brand-navy sm:text-5xl lg:text-6xl">
            Two ways to know where your goods are. One honest answer.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-brand-slate md:text-lg">
            A disposable Bluetooth smart sticker, or a 4G cellular label that reports its own
            position. We explain exactly how each one works — including its limits — then help you pilot the right one.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <PrimaryLink to="/request-a-pilot" testid="hero-cta-pilot">Discuss Your Shipment</PrimaryLink>
            <GhostLink to="/compare-smart-labels" testid="hero-cta-compare">Compare the two labels</GhostLink>
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Pill label="Bluetooth" tone="cyan" />
            <Pill label="4G Cellular + GNSS" tone="lime" />
          </div>
          </div>
          <img
            src="https://customer-assets-lqy194kg.emergentagent.net/job_pilot-launch-hub/artifacts/cmnd0nuk_gnts1-smartsticker3.png"
            alt="GNTS1 Smart Sticker S1 applied to a shrink-wrapped pallet in a warehouse aisle"
            className="hidden w-full rounded-sm border border-border object-cover lg:block"
            data-testid="hero-product-image"
          />
        </div>
      </section>

      {/* Two architectures */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8" data-testid="home-architectures">
        <SectionHead
          kicker="The two architectures"
          title="Same map. Completely different physics."
          sub="Both products appear as location events in GoAndTrack. What differs is how the event is produced — a partner detection network, or the label's own cellular connection — and that drives cost, battery life and where each fits."
        />
        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          {productList.map((p) => (
            <article key={p.id} className="flex flex-col rounded-sm border border-border bg-white p-8 transition-colors duration-150 hover:border-primary/50" data-testid={`home-card-${p.id}`}>
              <Pill label={p.pill.label} tone={p.pill.tone} />
              <h3 className="mt-5 text-2xl font-semibold tracking-tight text-brand-navy">{p.fullName}</h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-brand-slate">{p.proposition}</p>
              {p.networkStat && (
                <p className="mt-4 font-mono text-xs font-semibold text-cyan-800" data-testid="home-card-network-stat">{p.networkStat}</p>
              )}
              <ul className="mt-5 space-y-2">
                {p.bestFor.slice(0, 3).map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-brand-slate">
                    <span className={`mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full ${p.id === "gnts1" ? "bg-brand-cyan" : "bg-brand-lime"}`} aria-hidden="true" />
                    {b}
                  </li>
                ))}
              </ul>
              <Link
                to={p.slug}
                data-testid={`home-card-${p.id}-link`}
                className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline underline-offset-4"
              >
                Explore the {p.name} <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </article>
          ))}
        </div>
        <div className="mt-6"><DecisionGraphic /></div>
      </section>

      {/* Partner network */}
      <section className="border-t border-border" data-testid="home-partner-network">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8">
          <div className="grid items-start gap-10 lg:grid-cols-[1.4fr_1fr]">
            <div>
              <SectionHead
                kicker="The partner detection network"
                title="A smart sticker powered by the devices already moving through the world"
                sub="GNTS1 emits a secure, encrypted Bluetooth signal that can be detected by compatible devices across a rapidly expanding partner network of more than 100 million devices. Participating smartphones, access points, IoT infrastructure and vehicle telematics systems securely relay detection events to the cloud, where shipment visibility is delivered through GoAndTrack."
              />
              <p className="mt-6 max-w-3xl text-sm leading-relaxed text-brand-slate">
                No onboard GPS receiver or SIM card is required in the sticker. Location events are created when its
                Bluetooth signal encounters compatible participating infrastructure.
              </p>
              <div className="mt-8 max-w-3xl"><NetworkNote /></div>
            </div>
            <div className="rounded-sm border border-cyan-600/40 bg-cyan-50/50 p-8 text-center lg:mt-14" data-testid="home-network-stat">
              <p className="font-heading text-5xl font-semibold tracking-tight text-brand-navy sm:text-6xl">100M<span className="text-primary">+</span></p>
              <p className="mt-3 font-mono text-xs font-medium uppercase tracking-widest text-brand-slate">
                devices in the expanding detection network
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Selector */}
      <section className="border-y border-border bg-brand-surface" data-testid="home-selector">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8">
          <SectionHead
            kicker="60-second steer"
            title="Which label is your likely fit?"
            sub="Four questions about your shipments. You get a starting hypothesis — never an automatic verdict. Every result ends with a human validation step."
          />
          <div className="mt-12"><DecisionSelector /></div>
        </div>
      </section>

      {/* Honesty strip */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8" data-testid="home-honesty">
        <div className="grid gap-10 lg:grid-cols-3">
          {[
            { icon: Radio, t: "We say how location is produced", b: "Every product page explains exactly where position data comes from — a partner detection network or the label's own cellular connection — and what each one can't do." },
            { icon: ShieldCheck, t: "Unverified claims stay in brackets", b: "Prices, certificates and battery figures awaiting approval appear as [placeholders], not polished guesses." },
            { icon: SatelliteDish, t: "Pilot before purchase", b: "A structured pilot on your real lanes replaces vendor promises with your own data — before you commit at scale." },
          ].map(({ icon: Icon, t, b }) => (
            <div key={t}>
              <Icon className="h-7 w-7 text-primary" strokeWidth={1.75} aria-hidden="true" />
              <h3 className="mt-4 text-lg font-semibold text-brand-navy">{t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-brand-slate">{b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Use cases */}
      <section className="border-y border-border bg-brand-surface" data-testid="home-use-cases">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8">
          <SectionHead kicker="Use cases" title="Where smart labels earn their keep" />
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {USE_CASES.map((u) => (
              <Link key={u.slug} to={`/use-cases/${u.slug}`} data-testid={`home-usecase-${u.slug}`}
              className="group rounded-sm border border-border bg-white overflow-hidden transition-colors duration-150 hover:border-primary/50">
                <img src={u.images[0].url} alt={u.images[0].alt} loading="lazy"
                  className="aspect-[16/9] w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]" />
                <div className="p-6">
                <h3 className="text-lg font-semibold text-brand-navy group-hover:text-primary">{u.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-slate">{u.short}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                  Read the use case <ArrowRight className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden="true" />
                </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Insights teaser */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8" data-testid="home-insights">
        <SectionHead kicker="Insights" title="Read before you buy" />
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {ARTICLES.map((a) => (
            <Link key={a.slug} to={`/insights/${a.slug}`} data-testid={`home-article-${a.slug}`}
              className="group rounded-sm border border-border p-6 transition-colors duration-150 hover:border-primary/50">
              <p className="font-mono text-xs text-brand-slate">{a.readingTime} read</p>
              <h3 className="mt-2 text-lg font-semibold text-brand-navy group-hover:text-primary">{a.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-brand-slate">{a.description}</p>
            </Link>
          ))}
        </div>
      </section>

      <CTABand />
    </>
  );
}
